import { Context } from '@spa/components/pages/paid-membership/subscribe/interaction/context'
import { none, Option, Some, some } from 'fp-ts/lib/Option'

export type SignupStep = 'input-tel'
                       | 'cannot-use-the-tel'
                       | 'input-sms-auth-token'
                       | 'input-email'
                       | 'input-password'
                       | 'ready-to-apply'
                       | 'complete'
                       | 'failed-user-auth'

interface CompletedInteraction {
  readonly tel: Some<string>
  readonly email: Some<string>
  readonly password: Some<string>
}

export class SignupWithSmsAuthInteraction {

  public static initialize(): SignupWithSmsAuthInteraction {
    /**
     * NOTE: 初期化方法は、電話番号入力ステップでのemptyのみ提供
     *
     * 無料会員登録時にSMS認証を必須にするため、メアド＆パスワード入力はSMS認証が通った時だけ可能となる。
     * したがって、無料会員登録フローは必ず電話番号入力から始まる。
     * （ステップの途中でブラウザリロードした時なども電話番号入力からやり直し）
     * @see: https://my-color.esa.io/posts/733
     */
    return this.emptyWith('input-tel')
  }

  private static emptyWith(step: SignupStep): SignupWithSmsAuthInteraction {
    return new SignupWithSmsAuthInteraction(
      none,
      false,
      none,
      none,
      step
    )
  }

  private constructor(
    private _tel: Option<string>,
    private _isTelAuthenticated: boolean,
    private _email: Option<string>,
    private _password: Option<string>,
    private _currentStep: SignupStep,
    private _step: number = 1
  ) {
  }

  get isInputTelStep(): boolean {
    return this._currentStep === 'input-tel'
  }

  get isCannotUseTheTelStep(): boolean {
    return this._currentStep === 'cannot-use-the-tel'
  }

  get isInputSmsAuthTokenStep(): boolean {
    return this._currentStep === 'input-sms-auth-token'
  }

  get isInputEmailStep(): boolean {
    return this._currentStep === 'input-email' || this._currentStep === 'failed-user-auth'
  }

  get isInputPasswordStep(): boolean {
    return this._currentStep === 'input-password'
  }

  get isRetryInputEmailStep(): boolean {
    return this._currentStep === 'failed-user-auth'
  }

  get tel(): Option<string> {
    return this._tel
  }

  get email(): Option<string> {
    return this._email
  }

  get password(): Option<string> {
    return this._password
  }

  get isTelAuthenticated(): boolean {
    return this._isTelAuthenticated
  }

  isCompleteInput(): this is CompletedInteraction {
    return this._currentStep === 'ready-to-apply'
  }

  cannotUseThePhoneNumber(): SignupWithSmsAuthInteraction {
    this.changeStep('cannot-use-the-tel')

    return this
  }

  inputTel(tel: string): SignupWithSmsAuthInteraction {
    this._tel = some(tel)
    this.changeStep('input-sms-auth-token')
    // tslint:disable-next-line:no-magic-numbers
    this._step = 2

    return this
  }

  succeedSMSAuth(): SignupWithSmsAuthInteraction {
    this._isTelAuthenticated = true
    this.changeStep('input-email')
    // tslint:disable-next-line:no-magic-numbers
    this._step = 3

    return this
  }

  failSMSAuth(): SignupWithSmsAuthInteraction {
    this._tel = none
    this.changeStep('input-tel')
    // tslint:disable-next-line:no-magic-numbers
    this._step = 1

    return this
  }

  inputEmail(email: string): SignupWithSmsAuthInteraction {
    this._email = some(email)
    this.changeStep('input-password')
    // tslint:disable-next-line:no-magic-numbers
    this._step = 4

    return this
  }

  inputPassword(password: string): SignupWithSmsAuthInteraction {
    this._password = some(password)
    this.changeStep('ready-to-apply')

    return this
  }

  succeedRegister(): SignupWithSmsAuthInteraction {
    this.changeStep('complete')

    return this
  }
  get iscomplete(): boolean {
    return this._currentStep === 'complete'
  }

  get context(): Context {
    return Context.valueOf(this._step)
  }

  failRegistration(): SignupWithSmsAuthInteraction {
    this._password = none
    this.changeStep('failed-user-auth')

    return this
  }

  private changeStep(step: SignupStep): void {
    this._currentStep = step
  }
}
